import React from 'react';
import './DistributionDonut.scss';

export interface DistributionDonutSegment {
  id: string;
  label: string;
  value: number;
  color: string;
}

export interface DistributionDonutProps {
  segments: DistributionDonutSegment[];
  centerValue: string;
  centerLabel: string;
  accessibleLabel?: string;
  size?: number;
  thickness?: number;
  valueFormatter?: (value: number) => string;
}

const DEFAULT_SIZE = 160;
const DEFAULT_THICKNESS = 18;

const defaultValueFormatter = (value: number) =>
  `${Number.isInteger(value) ? value : value.toFixed(1)}%`;

const DistributionDonut: React.FC<DistributionDonutProps> = ({
  segments,
  centerValue,
  centerLabel,
  accessibleLabel,
  size = DEFAULT_SIZE,
  thickness = DEFAULT_THICKNESS,
  valueFormatter = defaultValueFormatter,
}) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  // Visual zeros are omitted; the accessible summary retains every bucket.
  const visibleSegments = segments.filter((segment) => segment.value > 0);

  let offset = 0;
  const arcs = visibleSegments.map((segment) => {
    const fraction = total > 0 ? segment.value / total : 0;
    const arcLength = circumference * fraction;
    const dashArray = `${arcLength} ${circumference - arcLength}`;
    const dashOffset = -offset;
    offset += arcLength;

    return { segment, dashArray, dashOffset };
  });

  return (
    <div className="distribution-donut" style={{ width: size, height: size }}>
      <svg
        className="distribution-donut__svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={accessibleLabel}
      >
        <circle
          className="distribution-donut__track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={thickness}
          fill="none"
        />
        {arcs.map(({ segment, dashArray, dashOffset }) => (
          <circle
            key={segment.id}
            className="distribution-donut__segment"
            data-testid={`distribution-donut-segment-${segment.id}`}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={segment.color}
            strokeWidth={thickness}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        ))}
      </svg>
      <div className="distribution-donut__center" aria-hidden="true">
        <span className="distribution-donut__center-value">{centerValue}</span>
        <span className="distribution-donut__center-label">{centerLabel}</span>
      </div>
      <ul className="distribution-donut__summary sr-only">
        {segments.map((segment) => (
          <li key={segment.id}>
            {segment.label}: {valueFormatter(segment.value)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DistributionDonut;
